import { useState } from 'react';
import { toonAantal, type VoorraadItem } from '@boodschappenlijst/shared';

interface Props {
  voorraad: VoorraadItem[];
  onIsOp: (id: string) => void;
  onVerwijder: (id: string) => void;
  onAanpassen: (id: string, velden: { aantal?: number; minimum?: number | null }) => void;
}

/** Zit het product op of onder zijn drempel? `null` = geen drempel ingesteld. */
const isLaag = (item: VoorraadItem) => item.minimum !== null && item.aantal <= item.minimum;

export function VoorraadLijst({ voorraad, onIsOp, onVerwijder, onAanpassen }: Props) {
  // Welke regel staat er open om de drempel te wijzigen. Eén tegelijk: dit is
  // een instelling die je zelden aanraakt, geen veld dat altijd zichtbaar hoeft.
  const [bewerkt, setBewerkt] = useState<string | null>(null);

  return (
    <div className="vs-card" style={{ padding: 'var(--space-2)' }}>
      {voorraad.length === 0 ? (
        <p className="vs-empty">Nog niets in de voorraad.</p>
      ) : (
        <ul className="vs-list">
          {voorraad.map((item) => (
            <li key={item.id} className={`vs-row${isLaag(item) ? ' vs-row--laag' : ''}`}>
              <div className="vs-row__body">
                <div className="vs-row__naam">
                  {item.naam}
                  {isLaag(item) && <span className="vs-badge vs-badge--laag">bijna op</span>}
                </div>
                <div className="vs-row__meta">
                  {item.toegevoegdDoor}
                  {item.minimum !== null && ` · minimum ${item.minimum}`}
                </div>
              </div>

              <button
                className="vs-stapper"
                onClick={() => onAanpassen(item.id, { aantal: item.aantal - 1 })}
                disabled={item.aantal <= 1}
                aria-label={`Eén ${item.naam} eraf`}
                title="Er is er een weg"
              >
                −
              </button>
              <span className="vs-row__aantal">{toonAantal(item.aantal, item.eenheid)}</span>
              <button
                className="vs-stapper"
                onClick={() => onAanpassen(item.id, { aantal: item.aantal + 1 })}
                aria-label={`Eén ${item.naam} erbij`}
                title="Er is er een bij"
              >
                +
              </button>

              <button
                className="vs-icon-btn"
                onClick={() => setBewerkt(bewerkt === item.id ? null : item.id)}
                aria-label={`Drempel voor ${item.naam} instellen`}
                aria-expanded={bewerkt === item.id}
                title="Drempel instellen"
              >
                ⚙
              </button>
              <button className="vs-btn vs-btn--ghost vs-btn--sm" onClick={() => onIsOp(item.id)}>
                Is op
              </button>
              <button
                className="vs-icon-btn"
                onClick={() => onVerwijder(item.id)}
                aria-label={`${item.naam} verwijderen`}
                title="Verwijderen"
              >
                ✕
              </button>

              {bewerkt === item.id && (
                <form
                  className="vs-drempel"
                  onSubmit={(e) => {
                    e.preventDefault();
                    const veld = new FormData(e.currentTarget).get('minimum');
                    const rauw = typeof veld === 'string' ? veld.trim() : '';
                    onAanpassen(item.id, { minimum: rauw === '' ? null : Number(rauw) });
                    setBewerkt(null);
                  }}
                >
                  <label className="vs-label" htmlFor={`min-${item.id}`}>
                    Waarschuw onder
                  </label>
                  <input
                    id={`min-${item.id}`}
                    name="minimum"
                    className="vs-input"
                    type="number"
                    min="0"
                    step="any"
                    inputMode="decimal"
                    defaultValue={item.minimum ?? ''}
                    placeholder="geen"
                  />
                  <button className="vs-btn vs-btn--sm" type="submit">
                    Opslaan
                  </button>
                </form>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
